import {
  faCheck,
  faCircleNodes,
  faLink,
} from "@fortawesome/pro-regular-svg-icons";
import { Icon } from "@intility/bifrost-react";
import type { Columns } from "@intility/gjallarbru";
import { DataTable } from "@intility/gjallarbru";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import { useClients } from "../../api/clients";
import { usePlatforms } from "../../api/platforms";
import type { ClientListItem } from "../../api/types";
import { clientPath } from "../../lib/clientPath";
import { clientKindLabel, formatDate } from "../../lib/clientTypes";
import { ClientPurposeBadge } from "../ClientTypeBadge";
import { GoToTag } from "../GoToTag";
import { RoleBadge } from "../RoleBadge";

type Row = { row: { original: ClientListItem } };

const WIRED_UP = "Wired up";
const NEEDS_WIRE_UP = "Needs to wire up";

// The default for the column slot. A literal `[]` in the parameter list is a new
// array per render, which would rebuild the columns every render.
const NO_COLUMNS: Columns<ClientListItem> = [];

// The kind of a client is its purpose (API, SPA, AI Agent, MCP, Worker); a
// client with none says its type.
function kindLabel(client: ClientListItem): string {
  return clientKindLabel(client.appType, client.purposeTag);
}

// The registry-style clients list. Owns its columns and row navigation, so a
// surface that lists clients renders this table and only decides which clients
// it holds (every client on the Clients page, the signed-in user's own on the
// My Hub tab).
export function ClientsTable({
  clients,
  columnsBefore = NO_COLUMNS,
  enablePagination = true,
  name = "clients",
  navigateState,
  rowClassName,
  showCreated = true,
  toolbarRadius = true,
}: {
  // Omitted: every client. Given: only that list.
  clients?: ClientListItem[];
  // The Created column; My Hub leaves it out.
  showCreated?: boolean;
  // Columns before the table's own, such as My Hub's favourite star.
  columnsBefore?: Columns<ClientListItem>;
  enablePagination?: boolean;
  // Names the table's saved column layout, so each table keeps its own.
  name?: string;
  // Router state passed when a row is opened (My Hub passes where it came from).
  navigateState?: unknown;
  rowClassName?: string;
  toolbarRadius?: boolean;
}) {
  const navigate = useNavigate();
  const { data: allClients } = useClients();
  const { data: platforms } = usePlatforms();
  const list = clients ?? allClients;

  const platformNameById = useMemo(
    () => new Map((platforms ?? []).map((p) => [p.id, p.name])),
    [platforms],
  );

  // Kinds present in the data drive the Kind filter options, so the list stays
  // in sync with whatever purpose tags actually exist.
  const kindOptions = useMemo(() => {
    const present = new Set((list ?? []).map((c) => kindLabel(c)));
    return [...present].sort().map((label) => ({ label, value: label }));
  }, [list]);

  // The Platform filter's options: "No platform" first, in italics (its value is
  // empty, which gjallarbru matches against the clients with no platform), then
  // every platform by name. gjallarbru types a label as text, but renders what
  // it is given.
  const platformOptions = useMemo(
    () => [
      { label: (<em>No platform</em>) as unknown as string, value: "" },
      ...[...new Set((platforms ?? []).map((p) => p.name))]
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ label: name, value: name })),
    ],
    [platforms],
  );

  const columns: Columns<ClientListItem> = useMemo(
    () => [
      ...columnsBefore,
      {
        accessorKey: "name",
        header: "Name",
        meta: { primaryColumn: true },
        size: 220,
      },
      {
        accessorKey: "clientId",
        Cell: ({ row }: Row) => <code>{row.original.clientId}</code>,
        header: "Client ID",
        size: 240,
      },
      {
        id: "kind",
        accessorFn: (row: ClientListItem) => kindLabel(row),
        // Only the badge: it says the purpose, in the colour of the client type.
        Cell: ({ row }: Row) => (
          <ClientPurposeBadge
            appType={row.original.appType}
            purposeTag={row.original.purposeTag}
          />
        ),
        filterSelectOptions: kindOptions,
        filterVariant: "select",
        header: "Kind",
        size: 220,
      },
      {
        id: "platform",
        accessorFn: (row: ClientListItem) =>
          row.platformId
            ? (platformNameById.get(row.platformId) ?? row.platformId)
            : "",
        Cell: ({ row }: Row) => {
          const platformId = row.original.platformId;
          if (!platformId) return null;
          // A tag with the platform icon; it opens a menu that goes to the
          // platform, and goes nowhere by itself.
          return (
            <GoToTag
              kind="platform"
              onGo={() =>
                navigate(`/platforms/${platformId}`, { state: navigateState })
              }
              title={platformNameById.get(platformId) ?? platformId}
            >
              <Icon icon={faCircleNodes} marginRight />
              {platformNameById.get(platformId) ?? platformId}
            </GoToTag>
          );
        },
        header: "Platform",
        // gjallarbru's own multi-select filter, with every platform as an option
        // and "No platform" on top.
        filterVariant: "multi-select",
        filterSelectOptions: platformOptions,
        size: 200,
      },
      {
        id: "userRole",
        // The list endpoint works out the caller's own role on each client
        // (two batch checks, not a read per client).
        accessorFn: (row: ClientListItem) => row.userRole,
        Cell: ({ row }: Row) => <RoleBadge role={row.original.userRole} />,
        header: "Role",
        // gjallarbru's multi-select filter; its options are the roles present.
        filterVariant: "multi-select",
        size: 140,
      },
      {
        id: "status",
        // The wired-up claim (ADR-0060): a maintainer's self-reported assertion
        // that a real application authenticates through the client.
        accessorFn: (row: ClientListItem) =>
          row.wiredUpAt ? WIRED_UP : NEEDS_WIRE_UP,
        // Italic and base 2, with the icon of each state.
        Cell: ({ row }: Row) => {
          const done = row.original.wiredUpAt != null;
          return (
            <span className="bfc-base-2" style={{ fontStyle: "italic" }}>
              <Icon icon={done ? faCheck : faLink} marginRight />
              {done ? WIRED_UP : NEEDS_WIRE_UP}
            </span>
          );
        },
        header: "Status",
        filterVariant: "multi-select",
        size: 180,
      },
      ...(showCreated
        ? [
            {
              accessorKey: "createdAt",
              Cell: ({ row }: Row) => (
                <span>{formatDate(row.original.createdAt)}</span>
              ),
              header: "Created",
              size: 140,
            },
          ]
        : []),
    ],
    [
      showCreated,
      columnsBefore,
      navigate,
      navigateState,
      platformNameById,
      platformOptions,
      kindOptions,
    ],
  );

  return (
    <DataTable
      // Passing onClick gives each row the hover background and pointer
      // cursor, and skips clicks on buttons and links inside the row (the
      // Platform link handles its own click).
      bifrostTableRowProps={{
        className: rowClassName,
        onClick: (
          _event: React.MouseEvent | React.KeyboardEvent,
          row: { original: ClientListItem },
        ) => {
          navigate(clientPath(row.original), { state: navigateState });
        },
      }}
      columns={columns}
      data={list ?? []}
      emptyState={{ title: "No clients match." }}
      name={name}
      tableOptions={{ enablePagination }}
      toolbarRadius={toolbarRadius}
    />
  );
}
